import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import { Button, Col, Dropdown, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { faBuilding, faBriefcase } from "@fortawesome/free-solid-svg-icons";

import CheckBox from '../../common/components/CheckBox';
import FormSelect from '../../common/components/form/FormSelect';
import FormNumericInput from '../../common/components/FormNumericInput';
import FormTextInput from "../../common/components/FormTextInput";
import SectionTogglePanel from "../../common/components/SectionTogglePanel";
import { getSuppliers } from "../suppliers/supplierSlice";
import OrganizationEditFilterForm from "./OrganizationEditFilterForm";
import { getDefaultDisplayModeFilters } from "../../common/metadata/FilterConfig";
import config from "../../config";
import { tokens } from "../../common/styles/design-tokens";

// When false, an organization is limited to the Contractor Universe only.
const MULTI_PRODUCT_ENABLED = !!config?.features?.multiProduct;

// UI-only metadata per product. Display labels come from the API
// (base-subscription `name`, sourced from the DB product table) — see the
// `name` field on each entry in `src`. Icon and `required` are presentation
// concerns owned here. Add an entry to surface a new product in the admin UI.
const PRODUCT_UI = {
    contractor_universe: {
        icon: faBuilding,
        required: true, // cannot be removed from a client
    },
    business_universe: {
        icon: faBriefcase,
        required: false,
    },
};

// Controls display order — contractor always first.
const PRODUCT_ORDER = ['contractor_universe', 'business_universe'];

// Engage export defaults seeded when a subscription has no saved value.
const DEFAULT_EXPORT_RECORD_LIMIT = 200000;
const DEFAULT_DOWNLOAD_MAX_FILE_SIZE = 4900000000;

// Pure helpers for mapping select values to/from the subscription payload.
const getValueFromObj = (obj) => obj?.map(a => a.value);
const emptyArrToNull = (obj) => ((obj ?? []).length === 0 ? null : obj);
const emptyArrToUnrestricted = (obj) => ((obj ?? []).length === 0 ? 'Unrestricted' : obj);

// Full per-product configuration section (collapsible).
function ProductConfigSection({
    productKey,
    label,
    initialData,
    onSubscriptionChange,
    onRemove,
    isDefault,
    onSetDefault,
    formValid,
    supplierList,
    classificationList,
    msaList,
    regionList,
    hasExistingSubscription,
}) {
    const ui = PRODUCT_UI[productKey];

    const [subscribe, setSubscribe] = useState(null);
    const [score, setScore] = useState();
    const [scoresAllowUnfiltered, setScoresAllowUnfiltered] = useState(null);
    const [classifications, setClassifications] = useState(null);
    const [msa, setMsa] = useState(null);
    const [regions, setRegions] = useState(null);
    const [enableExplore, setEnableExplore] = useState(null);
    const [supplierOverlay, setSupplierOverlay] = useState(null);
    const [suppliers, setSuppliers] = useState(null);
    const [previewFilter, setPreviewFilter] = useState(null);
    const [enableEnrich, setEnableEnrich] = useState(null);
    const [importRecordLimit, setImportRecordLimit] = useState(null);
    const [importMaxFileSize, setImportMaxFileSize] = useState(null);
    const [enableEngage, setEnableEngage] = useState(null);
    const [exportRecordLimit, setExportRecordLimit] = useState(DEFAULT_EXPORT_RECORD_LIMIT);
    const [downloadMaxFileSize, setDownloadMaxFileSize] = useState(DEFAULT_DOWNLOAD_MAX_FILE_SIZE);
    const [enableChat, setEnableChat] = useState(null);
    const [customFilters, setCustomFilters] = useState([]);

    const [localInitialized, setLocalInitialized] = useState(false);
    const didInitRef = useRef(false);
    // productId is DB-generated and never edited here. Capture it once at init so the
    // output effect doesn't need `initialData` as a dependency — depending on it caused
    // an infinite render loop, because the parent feeds the emitted subscription back
    // down as a new `initialData` object reference on every emit.
    const productIdRef = useRef(undefined);

    useEffect(() => {
        if (didInitRef.current) return;

        // initialData is undefined for newly added products — use defaults.
        const data = initialData ?? {};
        productIdRef.current = data.productId;

        setSubscribe(data.subscribed ?? null);
        setScore(data.restrictions?.scores);
        setScoresAllowUnfiltered(data.restrictions?.scoresAllowUnfiltered ?? null);

        const optionMap = new Map(classificationList.map(o => [o.value, o]));
        setClassifications(
            data.restrictions?.tradeClassifications === 'Unrestricted' ? null :
            (data.restrictions?.tradeClassifications ?? []).map(id => optionMap.get(id)).filter(Boolean)
        );

        const optionMapMSA = new Map(msaList.map(o => [o.value, o]));
        setMsa(
            data.restrictions?.metropolitanStatisticalAreas === 'Unrestricted' ? null :
            (data.restrictions?.metropolitanStatisticalAreas ?? []).map(id => optionMapMSA.get(id)).filter(Boolean)
        );

        const optionMapRegions = new Map(regionList.map(o => [o.value, o]));
        setRegions(
            data.restrictions?.regions === 'Unrestricted' ? null :
            (data.restrictions?.regions ?? []).map(id => optionMapRegions.get(id)).filter(Boolean)
        );

        const selectedIds = data.features?.explore?.supplierOverlays?.selected;
        setSuppliers(
            Array.isArray(selectedIds)
                ? selectedIds.map(id => supplierList.find(s => s.value === id)).filter(Boolean)
                : null
        );
        setSupplierOverlay(!!selectedIds);

        setEnableExplore(data.features?.explore?.enabled ?? null);
        setPreviewFilter(data.features?.explore?.showPreviewFilters ?? null);
        setEnableEnrich(data.features?.enrich?.enabled ?? null);
        setImportRecordLimit(data.features?.enrich?.listImportRecordLimit ?? null);
        setImportMaxFileSize(data.features?.enrich?.listImportMaxFileSize ?? null);
        setEnableEngage(data.features?.engage?.enabled ?? null);
        setExportRecordLimit(data.features?.engage?.listExportRecordLimit ?? DEFAULT_EXPORT_RECORD_LIMIT);
        setDownloadMaxFileSize(data.features?.engage?.listExportMaxFileSize ?? DEFAULT_DOWNLOAD_MAX_FILE_SIZE);
        setEnableChat(data.features?.chat?.enabled ?? null);

        // Seed any filter that declares a defaultDisplayMode (e.g. COMPANY_ID/
        // CONTACT_ID/BUILDER_ID defaulting to Hidden) as an explicit override,
        // unless this org already has its own saved entry for that FILTER_ID.
        // Without this, the default only ever applied as a client-side render
        // fallback and was never actually written into the org's subscription
        // on save — this makes saving (create or edit, untouched) persist the
        // same explicit entry a manually-changed dropdown would.
        const existingFilters = data.filters ?? [];
        const existingFilterIds = new Set(existingFilters.map(f => f.FILTER_ID));
        const missingDefaults = getDefaultDisplayModeFilters(productKey)
            .filter(f => !existingFilterIds.has(f.FILTER_ID));
        setCustomFilters([...existingFilters, ...missingDefaults]);

        setLocalInitialized(true);
        didInitRef.current = true;
    }, [initialData, classificationList, msaList, regionList, supplierList, productKey]);

    useEffect(() => {
        if (!localInitialized) return;

        onSubscriptionChange(productKey, {
            // productId is DB-generated and owned by the backend. Echo back the id
            // captured at init (see productIdRef) rather than reading `initialData`
            // here, so this effect need not depend on the churning initialData ref.
            productId: productIdRef.current,
            productKey,
            subscribed: subscribe,
            restrictions: {
                scores: score,
                scoresAllowUnfiltered,
                tradeClassifications: emptyArrToUnrestricted(!subscribe ? null : getValueFromObj(classifications)),
                metropolitanStatisticalAreas: emptyArrToUnrestricted(!subscribe ? null : getValueFromObj(msa)),
                regions: emptyArrToUnrestricted(!subscribe ? null : getValueFromObj(regions)),
            },
            features: {
                explore: {
                    enabled: enableExplore,
                    supplierOverlays: {
                        selected: emptyArrToNull(getValueFromObj(suppliers)),
                        defaultColor: tokens.colors.blue,
                    },
                    showPreviewFilters: previewFilter,
                },
                enrich: {
                    enabled: enableEnrich,
                    listImportRecordLimit: importRecordLimit,
                    listImportMaxFileSize: importMaxFileSize,
                },
                engage: {
                    enabled: enableEngage,
                    listExportRecordLimit: exportRecordLimit,
                    listExportMaxFileSize: downloadMaxFileSize,
                },
                chat: { enabled: enableChat },
            },
            filters: customFilters,
        });
    }, [
        // NOTE: `initialData` intentionally excluded — see productIdRef above.
        localInitialized, productKey,
        subscribe, score, scoresAllowUnfiltered, classifications, msa, regions,
        enableExplore, suppliers, previewFilter,
        enableEnrich, importRecordLimit, importMaxFileSize,
        enableEngage, exportRecordLimit, downloadMaxFileSize,
        enableChat, customFilters, onSubscriptionChange,
    ]);

    return (
        <SectionTogglePanel
            title={label}
            icon={ui?.icon}
            appearance="card"
            square={true}
            isExpand={true}
            overflowX={false}
        >
            <Row style={{ marginBottom: '8px', alignItems: 'center', marginTop: '8px' }}>
                <Col md={2}>
                    <CheckBox
                        id={`${productKey}-subscribe`}
                        label="Subscribe"
                        checked={subscribe}
                        onChange={setSubscribe}
                        size="sm"
                    />
                </Col>
                <Col md={2}>
                    <CheckBox
                        id={`${productKey}-set-as-default`}
                        label="Set as Default"
                        checked={isDefault}
                        onChange={checked => { if (checked) onSetDefault(productKey); }}
                        size="sm"
                    />
                </Col>
                {!ui?.required && (
                    <Col className="text-end">
                        <Button variant="outline-danger" size="sm" onClick={() => onRemove(productKey)}>
                            Remove
                        </Button>
                    </Col>
                )}
            </Row>

            <div style={{ border: '0.5px ridge', padding: '12px' }}>
                <Row>
                    <Col md={6}>
                        <FormLabel className="formTextLabel" style={{ marginTop: '12px' }}>Restrictions</FormLabel>
                        <div style={{ border: '0.5px ridge', padding: '12px' }}>
                            <Row>
                                <FormGroup as={Col} controlId={`${productKey}-score`}>
                                    <FormNumericInput
                                        label="Score"
                                        placeholder="Enter score"
                                        value={score}
                                        onChange={setScore}
                                        max={0.9}
                                        min={0.6}
                                        isInvalid={!formValid && !(score >= 0.6 && score <= 0.9)}
                                        size="sm"
                                        required
                                        height={36}
                                    />
                                </FormGroup>
                            </Row>
                            <Row>
                                <Col md={9}>
                                    <CheckBox
                                        id={`${productKey}-scores-allow-unfiltered`}
                                        label="Scores - Allow Unfiltered"
                                        checked={scoresAllowUnfiltered}
                                        onChange={setScoresAllowUnfiltered}
                                        isInvalid={!formValid && !(scoresAllowUnfiltered === true || scoresAllowUnfiltered === false)}
                                        size="sm"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={9}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <FormSelect
                                            label="Trade Classifications"
                                            placeholder="Unrestricted"
                                            value={classifications}
                                            onChange={setClassifications}
                                            options={classificationList}
                                            isMulti={true}
                                            size="sm"
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={9}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <FormSelect
                                            label="Metropolitan Statistical Areas (MSA)"
                                            placeholder="Unrestricted"
                                            value={msa}
                                            onChange={setMsa}
                                            options={msaList}
                                            isMulti={true}
                                            size="sm"
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={9}>
                                    <FormSelect
                                        label="Regions (States)"
                                        placeholder="Unrestricted"
                                        value={regions}
                                        onChange={setRegions}
                                        options={regionList}
                                        isMulti={true}
                                        size="sm"
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Col>

                    <Col md={6}>
                        <FormLabel className="formTextLabel" style={{ marginTop: '12px' }}>Features</FormLabel>
                        <div style={{ border: '0.5px ridge', padding: '12px' }}>
                            <Row>
                                <FormGroup as={Col} controlId={`${productKey}-explore-group`}>
                                    <Col md={12}>
                                        <Row>
                                            <CheckBox
                                                id={`${productKey}-enable-explore`}
                                                label="Explore"
                                                onChange={setEnableExplore}
                                                checked={enableExplore}
                                                size="sm"
                                            />
                                        </Row>
                                        <Row>
                                            <Col md={1} />
                                            <Col md={5}>
                                                <CheckBox
                                                    id={`${productKey}-supplier-overlay`}
                                                    label="Supplier Overlay"
                                                    onChange={value => {
                                                        setSupplierOverlay(value);
                                                        if (!value) setSuppliers(null);
                                                    }}
                                                    checked={supplierOverlay}
                                                    size="sm"
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={1} />
                                            <Col md={11}>
                                                <FormSelect
                                                    label="Suppliers"
                                                    placeholder="Select Suppliers"
                                                    value={suppliers}
                                                    onChange={value => {
                                                        if ((value ?? []).some(v => v.value === '__select_all__')) {
                                                            setSuppliers(supplierList);
                                                        } else {
                                                            setSuppliers(value);
                                                        }
                                                    }}
                                                    options={[{ value: '__select_all__', label: 'Select All' }, ...(supplierList ?? [])]}
                                                    isMulti={true}
                                                    size="sm"
                                                    disabled={!supplierOverlay}
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={1} />
                                            <Col md={5}>
                                                <CheckBox
                                                    id={`${productKey}-preview-filter`}
                                                    label="Preview Filters"
                                                    checked={previewFilter}
                                                    onChange={setPreviewFilter}
                                                    size="sm"
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </FormGroup>
                            </Row>

                            <Row>
                                <FormGroup as={Col} controlId={`${productKey}-enrich-group`}>
                                    <Col md={12}>
                                        <Row>
                                            <CheckBox
                                                id={`${productKey}-enable-enrich`}
                                                label="Enrich"
                                                checked={enableEnrich}
                                                onChange={setEnableEnrich}
                                                size="sm"
                                            />
                                        </Row>
                                        <Row>
                                            <Col md={1} />
                                            <Col md={11}>
                                                <FormTextInput
                                                    label="LIST IMPORT RECORD LIMIT"
                                                    value={importRecordLimit}
                                                    onChange={setImportRecordLimit}
                                                    isInvalid={!formValid && !(importRecordLimit && Number(importRecordLimit))}
                                                    size="sm"
                                                    height={36}
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={1} />
                                            <Col md={11}>
                                                <FormTextInput
                                                    label="LIST IMPORT MAX FILE SIZE"
                                                    value={importMaxFileSize}
                                                    onChange={setImportMaxFileSize}
                                                    isInvalid={!formValid && !(importMaxFileSize && Number(importMaxFileSize))}
                                                    size="sm"
                                                    height={36}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </FormGroup>
                            </Row>

                            <Row>
                                <FormGroup as={Col} controlId={`${productKey}-engage-group`}>
                                    <Col md={12}>
                                        <Row>
                                            <CheckBox
                                                id={`${productKey}-enable-engage`}
                                                label="Engage"
                                                checked={enableEngage}
                                                onChange={setEnableEngage}
                                                size="sm"
                                            />
                                        </Row>
                                        <Row>
                                            <Col md={1} />
                                            <Col md={11}>
                                                <FormTextInput
                                                    label="LIST EXPORT RECORD LIMIT"
                                                    value={exportRecordLimit}
                                                    onChange={setExportRecordLimit}
                                                    isInvalid={!formValid && !(exportRecordLimit && Number(exportRecordLimit))}
                                                    size="sm"
                                                    height={36}
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={1} />
                                            <Col md={11}>
                                                <FormTextInput
                                                    label="LIST DOWNLOAD MAX FILE SIZE"
                                                    value={downloadMaxFileSize}
                                                    onChange={setDownloadMaxFileSize}
                                                    isInvalid={!formValid && !(downloadMaxFileSize && Number(downloadMaxFileSize))}
                                                    size="sm"
                                                    height={36}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </FormGroup>
                            </Row>

                            <Row>
                                <FormGroup as={Col} controlId={`${productKey}-chat-group`}>
                                    <Col md={12}>
                                        <Row>
                                            <CheckBox
                                                id={`${productKey}-enable-chat`}
                                                label="Chat"
                                                checked={enableChat}
                                                onChange={setEnableChat}
                                                size="sm"
                                            />
                                        </Row>
                                    </Col>
                                </FormGroup>
                            </Row>
                        </div>
                    </Col>
                </Row>

                {hasExistingSubscription && (
                    <Row style={{ marginTop: '12px' }}>
                        <Col md={12}>
                            <SectionTogglePanel title="Filters" isExpand={true} overflowX={false}>
                                <OrganizationEditFilterForm
                                    productKey={productKey}
                                    setCustomFilters={setCustomFilters}
                                    customFilters={customFilters}
                                />
                            </SectionTogglePanel>
                        </Col>
                    </Row>
                )}
            </div>
        </SectionTogglePanel>
    );
}

function OrganizationConfigForm(props) {
    const {
        setConfig,
        setSubscription,
        defaultSubscription,
        formValid,
        subscription,
    } = props;

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(getSuppliers());
    }, [dispatch]);

    const metaDataState = useSelector(state => state.metaData);
    const suppliersState = useSelector(state => state.suppliers);

    const regionList = useMemo(() => (metaDataState['companyAddressRegion'] ?? []).map(e => ({ value: e.id, label: e.display })), [metaDataState]);
    const msaList = useMemo(() => (metaDataState['companyAddressLocMsa'] ?? []).map(e => ({ value: e.id, label: e.display })), [metaDataState]);
    const classificationList = useMemo(() => (metaDataState['companyClassTrades'] ?? []).map(e => ({ value: e.id, label: e.display })), [metaDataState]);
    const supplierList = useMemo(() => suppliersState.suppliers?.map(sup => ({ value: sup.id, label: sup.label })) ?? [], [suppliersState]);

    const src = subscription ?? defaultSubscription;

    // Product display name is canonical metadata from the API (base-subscription,
    // sourced from the DB product table). The org's saved subscription may predate
    // the `name` field, so prefer defaultSubscription, then src, then the raw key.
    const productName = (key) => defaultSubscription?.[key]?.name ?? src?.[key]?.name ?? key;

    // activeProductKeys is always ordered: contractor first, then others in PRODUCT_ORDER.
    const [activeProductKeys, setActiveProductKeys] = useState([]);
    // Which product key currently has default: true.
    const [defaultProductKey, setDefaultProductKey] = useState('contractor_universe');
    // Accumulated subscription data reported by each ProductConfigSection.
    const [productData, setProductData] = useState({});

    // Subscription keys not in PRODUCT_UI are preserved as-is.
    const unknownDataRef = useRef({});
    // Guards the output useEffect — prevents emitting before real data is loaded.
    const [initialized, setInitialized] = useState(false);
    const didInitRef = useRef(false);

    useEffect(() => {
        if (didInitRef.current) return;
        // null/undefined means still loading — wait. {} (or populated) means ready.
        if (src === null || src === undefined) return;

        const preserved = Object.entries(src).filter(([k]) => !(k in PRODUCT_UI));

        // New org (no existing subscription) starts with contractor only; the admin
        // adds other products afterward. Editing shows whatever products the org has.
        const isEditing = !!subscription;
        let activeKeys;
        if (!MULTI_PRODUCT_ENABLED) {
            // Multi-product disabled: only contractor is editable. Preserve any other
            // existing product subscriptions untouched so saving doesn't wipe them.
            activeKeys = ['contractor_universe'];
            Object.entries(src).forEach(([k, v]) => {
                if (k in PRODUCT_UI && k !== 'contractor_universe') {
                    preserved.push([k, v]);
                }
            });
        } else if (isEditing) {
            activeKeys = PRODUCT_ORDER.filter(k => k in PRODUCT_UI && src[k]);
            if (!activeKeys.includes('contractor_universe')) {
                activeKeys.unshift('contractor_universe');
            }
        } else {
            activeKeys = ['contractor_universe'];
        }
        unknownDataRef.current = Object.fromEntries(preserved);
        setActiveProductKeys(activeKeys);

        // Honour the saved default flag; fall back to contractor.
        const savedDefault = activeKeys.find(k => src[k]?.default === true) ?? 'contractor_universe';
        setDefaultProductKey(savedDefault);

        setInitialized(true);
        didInitRef.current = true;
    }, [src, subscription]);

    // Called by each ProductConfigSection whenever its internal state changes.
    const handleProductChange = useCallback((productKey, data) => {
        setProductData(prev => ({ ...prev, [productKey]: data }));
    }, []);

    const handleAddProduct = useCallback((productKey) => {
        setActiveProductKeys(prev => {
            // Maintain PRODUCT_ORDER ordering.
            const next = [...prev, productKey];
            return PRODUCT_ORDER.filter(k => next.includes(k));
        });
    }, []);

    const handleRemoveProduct = useCallback((productKey) => {
        setActiveProductKeys(prev => prev.filter(k => k !== productKey));
        setProductData(prev => {
            const next = { ...prev };
            delete next[productKey];
            return next;
        });
        // If the removed product was the default, fall back to contractor.
        setDefaultProductKey(prev => prev === productKey ? 'contractor_universe' : prev);
    }, []);

    const handleSetDefault = useCallback((productKey) => {
        setDefaultProductKey(productKey);
    }, []);

    // Emit the combined subscription whenever any product reports a change.
    // Waits until every active product has reported at least once.
    useEffect(() => {
        if (!initialized) return;
        if (!activeProductKeys.every(k => productData[k])) return;

        const newSubscription = { ...unknownDataRef.current };
        activeProductKeys.forEach(k => {
            newSubscription[k] = {
                ...productData[k],
                default: k === defaultProductKey,
            };
        });

        setSubscription(newSubscription);
        setConfig(undefined);
    }, [initialized, activeProductKeys, productData, defaultProductKey, setSubscription, setConfig]);

    const availableToAdd = MULTI_PRODUCT_ENABLED
        ? PRODUCT_ORDER.filter(k => k in PRODUCT_UI && !activeProductKeys.includes(k))
        : [];

    return (
        <div>
            <SectionTogglePanel title="SUBSCRIPTIONS" isExpand={true} overflowX={false}>
                <div style={{ marginTop: '16px' }} />
                {activeProductKeys.map(productKey => (
                    <ProductConfigSection
                        key={productKey}
                        productKey={productKey}
                        label={productName(productKey)}
                        initialData={src?.[productKey] ?? defaultSubscription?.[productKey]}
                        onSubscriptionChange={handleProductChange}
                        onRemove={handleRemoveProduct}
                        isDefault={productKey === defaultProductKey}
                        onSetDefault={handleSetDefault}
                        formValid={formValid}
                        supplierList={supplierList}
                        classificationList={classificationList}
                        msaList={msaList}
                        regionList={regionList}
                        hasExistingSubscription={!!src?.[productKey]}
                    />
                ))}

                {availableToAdd.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                        <Dropdown>
                            <Dropdown.Toggle variant="outline-secondary" size="sm" id="add-product-dropdown">
                                + Add Product
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {availableToAdd.map(productKey => (
                                    <Dropdown.Item
                                        key={productKey}
                                        onClick={() => handleAddProduct(productKey)}
                                    >
                                        {productName(productKey)}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                )}
            </SectionTogglePanel>
        </div>
    );
}

export default React.memo(OrganizationConfigForm);
